import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  Cpu,
  HelpCircle,
  Hexagon,
  KeyRound,
  MailQuestion,
  Users,
  UserRound,
  X,
} from "lucide-react";
import { format } from "date-fns";
import {
  memo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import type { MFReviewAccount } from "state/members/accessReviews";
import { appDisplayName } from "modules/apps/tenants";
import { Avatar, Button } from "components/ds";
import {
  ReviewChip,
  getButtonStyle,
  isAccountLocked,
} from "../Table/AccountRow";
import {
  accountDrawerNav,
  selectableOutcomeOptions,
  isQueried,
  showsAdminResponse,
  activityBand,
  activityUnavailableLabel,
  peerSentence,
  hasStage1Context,
  RAISE_QUERY_ACTION,
  resolveLastAccessed,
  stage1ContextHint,
  accountCellName,
  isNhiAccount,
} from "../reviewLogic";

/**
 * AccountDrawer — a focused per-account detail view (PLO-4818 phase 2, item 4),
 * opened from a table row's expand affordance. Shows the account's identity,
 * access, role and activity in a sectioned profile sheet (label → value-pill
 * rows), and lets the reviewer page through the same flat, filtered, ordered
 * account list the table renders via prev/next (position + neighbours come from
 * reviewLogic.accountDrawerNav).
 *
 * When the review is editable, a footer bar mirrors the row's decision actions
 * so the reviewer can act in place (same handler as the table row).
 */

type OutcomeOption = {
  name: string;
  standard_mapping: string;
  requires_note?: boolean;
};

const fmtDate = (d?: string) => {
  if (!d) return "-";
  try {
    return format(new Date(d), "d MMM yyyy");
  } catch {
    return d;
  }
};

const lastUsedLabel = (v: MFReviewAccount["lastUsageDate"] | null): string => {
  if (!v) return "-";
  if (typeof v === "string") return fmtDate(v);
  return v.date ? fmtDate(v.date) : "-";
};

const sentenceCase = (value: string): string => {
  const text = value.replace(/_/g, " ").trim();
  if (!text) return "";
  return `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`;
};

// value-pill — white, 1px border, radius full (design E). `mono` renders the
// copyable external-id value in the monospace face.
const ValuePill = ({
  children,
  mono,
}: {
  children: React.ReactNode;
  mono?: boolean;
}) => (
  <Flex
    align="center"
    px="md"
    minH="26px"
    py="2px"
    borderRadius="full"
    bg="bg.container"
    border="1px solid"
    borderColor="border.default"
    maxW="100%"
  >
    <Text
      fontSize="smd"
      fontWeight={500}
      fontFamily={mono ? "mono" : undefined}
      whiteSpace="nowrap"
      overflow="hidden"
      textOverflow="ellipsis"
      color="text.body.primary"
    >
      {children}
    </Text>
  </Flex>
);

// mono value + copy-on-hover (external id, email).
const ClipboardValue = ({
  value,
  mono,
  label = "Copy value",
}: {
  value: string;
  mono?: boolean;
  label?: string;
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (event: ReactMouseEvent) => {
    event.stopPropagation();
    if (typeof navigator !== "undefined" && navigator.clipboard) {
      void navigator.clipboard.writeText(value);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  return (
    <HStack spacing="1xs" role="group" minW={0}>
      <Text
        fontSize="smd"
        fontFamily={mono ? "mono" : undefined}
        color="text.body.tertiary"
        noOfLines={1}
        title={value}
      >
        {value}
      </Text>
      <Tooltip label={copied ? "Copied" : label} placement="top" hasArrow>
        <IconButton
          aria-label={label}
          icon={<Icon as={Copy} boxSize="12px" />}
          size="xs"
          variant="ghost"
          minW="20px"
          h="20px"
          borderRadius="component.md"
          color="text.body.tertiary"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          _hover={{ bg: "neutral.100", color: "text.body.primary" }}
          transition="opacity 120ms, background-color 120ms, color 120ms"
          onClick={handleCopy}
        />
      </Tooltip>
    </HStack>
  );
};

const DetailSection = ({
  icon,
  label,
  children,
}: {
  icon: typeof KeyRound;
  label: string;
  children: React.ReactNode;
}) => (
  <Box>
    <HStack spacing="sm" mb="1xs">
      <Icon as={icon} boxSize="14px" color="text.body.tertiary" />
      <Text fontSize="smd" fontWeight={600} color="text.body.primary">
        {label}
      </Text>
    </HStack>
    <Box>{children}</Box>
  </Box>
);

const DetailRow = ({
  label,
  children,
  top,
}: {
  label: string;
  children: React.ReactNode;
  top?: boolean;
}) => (
  <Flex
    justify="space-between"
    align={top ? "flex-start" : "center"}
    py="sm"
    gap="md"
    borderBottom="1px solid"
    borderColor="border.subtle"
  >
    <Text
      fontSize="smd"
      color="text.body.tertiary"
      flexShrink={0}
      pt={top ? "2xs" : undefined}
    >
      {label}
    </Text>
    <Flex gap="2xs" minW={0} justify="flex-end" wrap="wrap">
      {children}
    </Flex>
  </Flex>
);

// tinted status pill (green for active).
const StatusPill = ({ status }: { status: string }) => {
  const active = status === "active";
  return (
    <Flex
      align="center"
      px="md"
      minH="26px"
      py="2px"
      borderRadius="full"
      bg={active ? "success.50" : "bg.section"}
      border="1px solid"
      borderColor={active ? "success.200" : "border.default"}
    >
      <Text
        fontSize="smd"
        fontWeight={500}
        textTransform="capitalize"
        color={active ? "success.700" : "text.body.secondary"}
      >
        {status}
      </Text>
    </Flex>
  );
};

// footer decision actions — mirrors the row's action set (design E). required /
// not_required render as labelled buttons; other outcomes as icon buttons. All
// disable when the account is locked (irreversible), matching the row (B3).
const FooterActions = ({
  account,
  outcomeOptions,
  onAction,
  isReadOnly,
  decisionOnly = false,
  reversibleOutcomes,
  entitlementOnly = false,
}: {
  account: MFReviewAccount;
  outcomeOptions: OutcomeOption[];
  onAction: (accountId: string, action: string) => void;
  isReadOnly?: boolean;
  // Cycle records decisions only — never deprovisions — so the footer actions
  // never lock and reviewers can re-decide (PLO-5083).
  decisionOnly?: boolean;
  // Locking outcomes this cycle never de-provisions — the footer actions stay
  // live for a re-decide on one of them (PLO-5749).
  reversibleOutcomes?: readonly string[];
  // Entitlement-only cycle (PLO-5731): the drawer is showing ONE entitlement,
  // so the flag-for-adjustment action is dropped from the footer too.
  entitlementOnly?: boolean;
}) => {
  const accountLocked = isAccountLocked(
    account,
    decisionOnly,
    reversibleOutcomes,
  );
  const locked = isReadOnly || accountLocked;
  const opts = selectableOutcomeOptions(outcomeOptions, entitlementOnly).slice(
    0,
    4,
  );
  return (
    <Flex
      px="1xl"
      py="md"
      borderTop="1px solid"
      borderColor="border.default"
      bg="bg.page"
      align="center"
      justify="flex-end"
      gap="sm"
      flexShrink={0}
    >
      {opts.map((o) => {
        const mapping = o.standard_mapping;
        // The drawer builds its own tooltip from the outcome's name below, so
        // it takes only the icon and colour from the shared style - the
        // entitlement-only tooltip rename has nothing to act on here.
        const style = getButtonStyle(mapping);
        const label =
          mapping === "required"
            ? "Mark required"
            : mapping === "not_required"
              ? "Mark not required"
              : `Mark ${o.name.toLowerCase()}`;
        const tooltip = isReadOnly
          ? "This review is read-only"
          : accountLocked
            ? "De-provisioning started: this can't be changed"
            : o.name;
        const labelled = mapping === "required" || mapping === "not_required";
        return labelled ? (
          <Button
            key={mapping}
            size="sm"
            variant="solid"
            tone="neutral"
            leadingIcon={
              <Icon as={style.icon} boxSize={3.5} color={style.color} />
            }
            isDisabled={locked}
            onClick={() => onAction(account.id, mapping)}
          >
            {label}
          </Button>
        ) : (
          <Tooltip key={mapping} label={tooltip} placement="top" hasArrow>
            <Button
              aria-label={label}
              size="sm"
              variant="solid"
              tone="neutral"
              icon={<Icon as={style.icon} boxSize={4} color={style.color} />}
              isDisabled={locked}
              onClick={() => onAction(account.id, mapping)}
            />
          </Tooltip>
        );
      })}
      {/* Raise a query — asks the campaign admin about this account. */}
      <Tooltip
        label="Raise a query with the campaign admin"
        placement="top"
        hasArrow
      >
        <Button
          aria-label="Raise a query"
          size="sm"
          variant="subtle"
          tone="neutral"
          icon={<Icon as={HelpCircle} boxSize={4} />}
          isDisabled={locked}
          onClick={() => onAction(account.id, RAISE_QUERY_ACTION)}
        />
      </Tooltip>
    </Flex>
  );
};

export interface AccountDrawerProps {
  account: MFReviewAccount | null;
  isOpen: boolean;
  onClose: () => void;
  /** flat, ordered account ids as currently displayed (filters/grouping applied) */
  orderedIds: string[];
  /** step to another account in the list */
  onNavigate: (accountId: string) => void;
  /** decision outcomes for the footer action set (omit to hide the footer) */
  outcomeOptions?: OutcomeOption[];
  /** true when the review is locked/read-only — hides the footer actions */
  isReadOnly?: boolean;
  /** fires the row's decision handler (opens the confirmation modal) */
  onAction?: (accountId: string, action: string) => void;
  /**
   * true when the cycle only records decisions and never deprovisions, so the
   * footer actions never lock and reviewers can re-decide (PLO-5083).
   */
  decisionOnly?: boolean;
  /**
   * Locking outcomes this cycle configures with no remediation action, so
   * recording one never de-provisioned anything. The footer actions stay live
   * for a re-decide on one of them in every review state (PLO-5749).
   */
  reversibleOutcomes?: readonly string[];
  /**
   * true in an entitlement-only cycle (PLO-5731), where the drawer is showing
   * ONE entitlement of the account: the flag-for-adjustment action is dropped
   * and a removal is labelled after the entitlement.
   */
  entitlementOnly?: boolean;
}

const AccountDrawer = memo(
  ({
    account,
    isOpen,
    onClose,
    orderedIds,
    onNavigate,
    outcomeOptions = [],
    isReadOnly = false,
    onAction,
    decisionOnly = false,
    reversibleOutcomes,
    entitlementOnly = false,
  }: AccountDrawerProps) => {
    // Trimmed so a blank value from the scanner doesn't render an empty row —
    // same contract as `identityDisplayValue` in reviewLogic (PLO-5750).
    const identityUsername = account?.identity?.username?.trim();
    const drawerBodyRef = useRef<HTMLDivElement>(null);
    const nav = accountDrawerNav(orderedIds, account?.id ?? null);
    const isNhi = !!account && isNhiAccount(account);
    const name = account ? accountCellName(account) : "";
    const entitlements = account?.entitlements ?? [];
    const queried = !!account && isQueried(account);
    const hasResponse = !!account && showsAdminResponse(account);

    // Stage-1 decision context (PLO-4806): populated on 2nd-review rows so the
    // SME sees what the first reviewer decided.
    const stage1 = account
      ? {
          review: account.stage1Review,
          note: account.stage1Note,
          reviewedAt: account.stage1ReviewedAt,
          reviewedByName: account.stage1ReviewedByName,
        }
      : null;
    const stage1Hint = stage1ContextHint(stage1);
    const showStage1 = hasStage1Context(stage1);

    // Peer-comparison label for the "in <dept>" clause; falls back to the
    // account-level group key, then snapshot department / job title.
    const peerGroupLabel =
      account?.peerComparison?.groupKey ??
      account?.snapshotDepartment ??
      account?.member.department ??
      account?.snapshotJobTitle ??
      account?.member.jobTitle ??
      null;

    // Full peer sentences per entitlement, filtered to those above the floor.
    const peerSentences = entitlements
      .map((e) =>
        peerSentence(
          e.snapshotEntitlementName,
          e.peerComparison,
          peerGroupLabel,
        ),
      )
      .filter((s): s is string => s !== null);

    // Last-accessed value + staleness (prefer the snapshot field). Normalise
    // first so the empty wrapper `{ date: null }` falls through to lastUsageDate
    // instead of shadowing it.
    const lastAccessedSource = resolveLastAccessed(
      account?.snapshotLastAccessedAt,
      account?.lastUsageDate,
    );
    const isStale = activityBand(lastAccessedSource) === "stale";
    const activityUnavailable = account
      ? activityUnavailableLabel(account)
      : null;

    // Role section only renders when there's a title or department (service
    // accounts skip it).
    const hasRole = !!(account?.member.jobTitle || account?.member.department);

    const showFooter = !!onAction && outcomeOptions.length > 0;

    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement="right"
        size="sm"
        initialFocusRef={drawerBodyRef}
      >
        <DrawerOverlay bg="blackAlpha.300" />
        <DrawerContent bg="bg.page">
          {account && (
            <>
              <DrawerHeader
                px="1xl"
                py="lg"
                borderBottom="1px solid"
                borderColor="border.default"
              >
                <Flex align="center" gap="md">
                  {/* prev/next across the ordered account list */}
                  <HStack spacing="2xs" flexShrink={0}>
                    <Tooltip
                      label="Previous account"
                      placement="bottom"
                      hasArrow
                    >
                      <Button
                        aria-label="Previous account"
                        icon={<Icon as={ChevronLeft} boxSize={4} />}
                        size="sm"
                        variant="subtle"
                        tone="neutral"
                        isDisabled={!nav.hasPrev}
                        onClick={() => nav.prevId && onNavigate(nav.prevId)}
                      />
                    </Tooltip>
                    <Tooltip label="Next account" placement="bottom" hasArrow>
                      <Button
                        aria-label="Next account"
                        icon={<Icon as={ChevronRight} boxSize={4} />}
                        size="sm"
                        variant="subtle"
                        tone="neutral"
                        isDisabled={!nav.hasNext}
                        onClick={() => nav.nextId && onNavigate(nav.nextId)}
                      />
                    </Tooltip>
                  </HStack>
                  {nav.total > 0 && (
                    <Text
                      fontSize="xs"
                      color="text.body.tertiary"
                      fontWeight={500}
                    >
                      {nav.index + 1} of {nav.total}
                    </Text>
                  )}
                  <Box flex="1" />
                  <Button
                    aria-label="Close"
                    icon={<Icon as={X} boxSize={4} />}
                    size="sm"
                    variant="subtle"
                    tone="neutral"
                    onClick={onClose}
                  />
                </Flex>

                <HStack spacing="md" align="center" mt="md">
                  {isNhi ? (
                    <Flex
                      w="40px"
                      h="40px"
                      borderRadius="full"
                      bg="p.100"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <Icon as={Cpu} boxSize={5} color="p.700" />
                    </Flex>
                  ) : (
                    <Avatar
                      name={name}
                      src={account.member.profilePictureUrl || undefined}
                      size="lg"
                    />
                  )}
                  <VStack align="flex-start" spacing="2xs" minW={0}>
                    <Text
                      fontFamily="heading"
                      fontSize="lg"
                      fontWeight={500}
                      color="text.body.primary"
                      noOfLines={1}
                    >
                      {name}
                    </Text>
                    {account.member.email && (
                      <ClipboardValue
                        value={account.member.email}
                        mono
                        label="Copy email"
                      />
                    )}
                  </VStack>
                </HStack>
              </DrawerHeader>

              <DrawerBody
                ref={drawerBodyRef}
                tabIndex={-1}
                px="1xl"
                py="lg"
                _focus={{ outline: "none" }}
              >
                <VStack align="stretch" spacing="1xl">
                  <DetailSection icon={UserRound} label="Identity">
                    {account.identity?.auth_type && (
                      <DetailRow label="Provider">
                        <ValuePill>
                          {sentenceCase(account.identity.auth_type)}
                        </ValuePill>
                      </DetailRow>
                    )}
                    {identityUsername && (
                      <DetailRow label="Username">
                        <ClipboardValue
                          value={identityUsername}
                          mono
                          label="Copy username"
                        />
                      </DetailRow>
                    )}
                    {account.identity?.extId && (
                      <DetailRow label="External ID">
                        <ClipboardValue
                          value={account.identity.extId}
                          mono
                          label="Copy external ID"
                        />
                      </DetailRow>
                    )}
                    {account.member.status && (
                      <DetailRow label="Status">
                        <StatusPill status={account.member.status} />
                      </DetailRow>
                    )}
                    <DetailRow label="Resource">
                      <ValuePill>{appDisplayName(account.resource)}</ValuePill>
                    </DetailRow>
                  </DetailSection>

                  <DetailSection icon={KeyRound} label="Access">
                    <DetailRow label="Review decision">
                      <ReviewChip
                        status={account.review}
                        outcomeOptions={outcomeOptions}
                        flaggedCount={account.flaggedCount}
                      />
                    </DetailRow>
                    <DetailRow label="Entitlements" top>
                      {entitlements.length ? (
                        entitlements.map((e) => (
                          <ValuePill key={e.id}>
                            {e.snapshotEntitlementName || "-"}
                          </ValuePill>
                        ))
                      ) : (
                        <Text fontSize="smd" color="text.body.tertiary">
                          -
                        </Text>
                      )}
                    </DetailRow>
                    {peerSentences.length > 0 && (
                      <DetailRow label="Peer comparison" top>
                        <VStack align="flex-end" spacing="1xs">
                          {peerSentences.map((sentence, i) => (
                            <HStack
                              key={i}
                              spacing="1xs"
                              align="flex-start"
                              justify="flex-end"
                            >
                              <Icon
                                as={Users}
                                boxSize={3.5}
                                color="text.body.tertiary"
                                mt="2px"
                                flexShrink={0}
                              />
                              <Text
                                fontSize="xs"
                                color="text.body.tertiary"
                                textAlign="right"
                              >
                                {sentence}
                              </Text>
                            </HStack>
                          ))}
                        </VStack>
                      </DetailRow>
                    )}
                  </DetailSection>

                  {showStage1 && (
                    <Box
                      bg="p.50"
                      border="1px solid"
                      borderColor="p.100"
                      borderRadius="component.2xl"
                      p="md"
                      data-testid="stage-one-context"
                    >
                      <Text
                        fontSize="xs"
                        fontWeight={600}
                        color="p.700"
                        mb="2xs"
                        textTransform="uppercase"
                        letterSpacing="0.02em"
                      >
                        First review
                      </Text>
                      {stage1Hint && (
                        <Text fontSize="smd" color="text.body.secondary">
                          {stage1Hint}
                        </Text>
                      )}
                      {stage1?.note && (
                        <Text
                          fontSize="smd"
                          color="text.body.tertiary"
                          whiteSpace="pre-wrap"
                          mt="2xs"
                        >
                          &ldquo;{stage1.note}&rdquo;
                        </Text>
                      )}
                    </Box>
                  )}

                  {queried && (
                    <DetailSection icon={MailQuestion} label="IAM query">
                      {account.note && (
                        <Box
                          bg="bg.section"
                          border="1px solid"
                          borderColor="border.default"
                          borderRadius="component.2xl"
                          p="md"
                          mt="sm"
                        >
                          <Text
                            fontSize="xs"
                            fontWeight={600}
                            color="text.body.tertiary"
                            mb="2xs"
                          >
                            Your question
                          </Text>
                          <Text
                            fontSize="smd"
                            color="text.body.secondary"
                            whiteSpace="pre-wrap"
                          >
                            {account.note}
                          </Text>
                        </Box>
                      )}
                      {hasResponse ? (
                        <Box
                          bg="p.50"
                          border="1px solid"
                          borderColor="p.100"
                          borderRadius="component.2xl"
                          p="md"
                          mt="sm"
                        >
                          <HStack
                            justify="space-between"
                            mb="2xs"
                            align="baseline"
                          >
                            <Text fontSize="xs" fontWeight={600} color="p.700">
                              IAM team response
                            </Text>
                            {account.queryResolvedAt && (
                              <Text fontSize="xs" color="p.600">
                                {fmtDate(account.queryResolvedAt)}
                              </Text>
                            )}
                          </HStack>
                          <Text
                            fontSize="smd"
                            color="text.body.secondary"
                            whiteSpace="pre-wrap"
                          >
                            {account.adminResponse}
                          </Text>
                        </Box>
                      ) : (
                        <Text fontSize="smd" color="text.body.tertiary" mt="sm">
                          Awaiting a response from the IAM team
                          {account.queriedAt
                            ? `, sent ${fmtDate(account.queriedAt)}`
                            : ""}
                          .
                        </Text>
                      )}
                    </DetailSection>
                  )}

                  {hasRole && (
                    <DetailSection icon={Hexagon} label="Role">
                      <DetailRow label="Job title">
                        <ValuePill>{account.member.jobTitle || "-"}</ValuePill>
                      </DetailRow>
                      <DetailRow label="Department">
                        <ValuePill>
                          {account.member.department || "-"}
                        </ValuePill>
                      </DetailRow>
                    </DetailSection>
                  )}

                  <DetailSection icon={Clock} label="Activity">
                    <DetailRow label="Last used">
                      {activityUnavailable ? (
                        <ValuePill>{activityUnavailable}</ValuePill>
                      ) : isStale ? (
                        <HStack spacing="1xs" justify="flex-end">
                          <Icon
                            as={AlertTriangle}
                            boxSize={3.5}
                            color="warning.500"
                            flexShrink={0}
                          />
                          <Text
                            fontSize="smd"
                            fontWeight={600}
                            color="warning.700"
                          >
                            {lastUsedLabel(lastAccessedSource)}
                          </Text>
                        </HStack>
                      ) : (
                        <ValuePill>
                          {lastUsedLabel(lastAccessedSource)}
                        </ValuePill>
                      )}
                    </DetailRow>
                    <DetailRow label="Access granted">
                      <ValuePill>{fmtDate(account.accessGrantedAt)}</ValuePill>
                    </DetailRow>
                    <DetailRow label="Access expires">
                      <ValuePill>
                        {account.isIndefinite
                          ? "Never"
                          : fmtDate(account.accessExpiresAt)}
                      </ValuePill>
                    </DetailRow>
                  </DetailSection>
                </VStack>
              </DrawerBody>

              {showFooter && (
                <FooterActions
                  account={account}
                  outcomeOptions={outcomeOptions}
                  onAction={onAction!}
                  isReadOnly={isReadOnly}
                  decisionOnly={decisionOnly}
                  reversibleOutcomes={reversibleOutcomes}
                  entitlementOnly={entitlementOnly}
                />
              )}
            </>
          )}
        </DrawerContent>
      </Drawer>
    );
  },
);

AccountDrawer.displayName = "AccountDrawer";

export default AccountDrawer;
